import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import OnScroll from './OnScroll';
import SMHeaderContext from './SMHeaderContext';
import { Addon } from './components/Addon';
import HeaderLogo from './components/HeaderLogo';
import { MobileDrawer } from './components/Drawer/Mobile';

import NavBarAnon from './anonymous/Desktop';
import MobileAnon from './anonymous/Mobile';
import { ValidMenuNameList } from './config/linkInfo';
import { MobileTabletScreen, DesktopScreen } from '../..';
import { HEADER_COLOR, HEADER_VARIANT } from './enums';

import './header.scss';
import './utils.scss';

const SMHeader = ({
  isUserAuthenticated,
  color,
  lang,
  variant,
  isEUDC,
  actionFlow,
  user,
  isAnonHeaderSticky,
  excludeMenuItems,
  showLoginButton,
  showSignupButton,
  Logo,
}) => {
  const LogoComponent = Logo || HeaderLogo;
  const auth = isUserAuthenticated ? 'auth' : 'anon';
  const headerColor = isUserAuthenticated ? 'primary' : color;
  const headerVariant = isUserAuthenticated ? 'default' : variant;

  return (
    <SMHeaderContext.Provider
      value={{
        isUserAuthenticated,
        lang,
        isEUDC,
        actionFlow,
        user,
        excludeMenuItems,
      }}
    >
      <OnScroll
        render={(x, y) => {
          const headerColorAnon = y > 0 ? 'light' : color;
          const smHeaderColor = isUserAuthenticated
            ? headerColor
            : headerColorAnon;

          const defaultColor =
            headerColorAnon === 'transparent' || headerColorAnon === 'primary';

          const logoColor = defaultColor ? 'alt' : 'primary';
          const mobileMenuColor = defaultColor ? 'alt' : 'secondary';
          const addonLogInColor = defaultColor ? 'alt' : 'secondary';
          const navBarColor = defaultColor ? 'alt' : 'secondary';
          const addonSignUpColor = defaultColor ? 'alt' : 'upgrade';
          const addonSignUpVariant = defaultColor ? 'ghost' : 'default';

          const classNames = classnames(
            `sm-header--${auth}`,
            `sm-header--${smHeaderColor}`,
            `sm-header--${headerVariant}`,
            { 'sm-header--en': lang === 'en' },
            {
              'sm-header--sticky': isAnonHeaderSticky && !isUserAuthenticated,
            }
          );

          return (
            <div className={classNames}>
              <MobileTabletScreen>
                <MobileDrawer
                  menuColor={mobileMenuColor}
                  data-testid="Header__MobileDrawer--Anon"
                >
                  <MobileAnon />
                </MobileDrawer>
              </MobileTabletScreen>
              <LogoComponent
                isUserAuthenticated={isUserAuthenticated}
                variant={variant}
                color={logoColor}
              />
              <DesktopScreen>
                <NavBarAnon color={navBarColor} lang={lang} />
              </DesktopScreen>
              <Addon
                addonSignUpColor={addonSignUpColor}
                addonLogInColor={addonLogInColor}
                addonSignUpVariant={addonSignUpVariant}
                variant={variant}
                isUserAuthenticated={isUserAuthenticated}
                showLoginButton={showLoginButton}
                showSignupButton={showSignupButton}
              />
            </div>
          );
        }}
      />
    </SMHeaderContext.Provider>
  );
};

SMHeader.propTypes = {
  isAnonHeaderSticky: PropTypes.bool,
  color: PropTypes.oneOf(Object.values(HEADER_COLOR)),
  isUserAuthenticated: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  lang: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(Object.values(HEADER_VARIANT)),
  isEUDC: PropTypes.bool.isRequired,
  actionFlow: PropTypes.string,
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  excludeMenuItems: PropTypes.arrayOf(PropTypes.oneOf(ValidMenuNameList)),
  showLoginButton: PropTypes.bool,
  showSignupButton: PropTypes.bool,
  Logo: PropTypes.node,
};

SMHeader.defaultProps = {
  isAnonHeaderSticky: true,
  color: 'primary',
  isUserAuthenticated: false,
  variant: 'default',
  actionFlow: 'header',
  excludeMenuItems: [],
  showLoginButton: true,
  showSignupButton: true,
  Logo: undefined,
};

export default SMHeader;
